import { useThemeMode } from '../../design-system/theme';
import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  Bell,
  BookOpen,
  Bot,
  Building2,
  CalendarDays,
  ChartNoAxesCombined,
  CreditCard,
  Flag,
  FlaskConical,
  GraduationCap,
  Handshake,
  KeyRound,
  Landmark,
  Layers3,
  LockKeyhole,
  Newspaper,
  Palette,
  Plug,
  Rocket,
  Search,
  ShieldCheck,
  Store,
  Trophy,
  UserRound,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarMenu,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '../../design-system/sidebar';
import { ANALYTICS_EVENTS, capturePostHogEvent } from '../../utils/analytics';
import { isExperimentPreviewAvailable } from '../../utils/experimentPreviewOverrides';
import './AuthenticatedSidebar.css';

type NavigationItem = {
  active?: (pathname: string) => boolean;
  contextTransition?: 'backward' | 'forward';
  icon: LucideIcon;
  label: string;
  to: string;
};

type NavigationContext = 'account' | 'product';

type NavigationGroup = {
  context: NavigationContext;
  items: NavigationItem[];
  label: string;
};

type AuthenticatedSidebarProps = {
  aiBootcampPath: string;
  challengesPath: string;
  currentPath: string;
  logoSrc: string;
  onLogout: () => void;
  preloadRoute?: (path: string) => void;
  userId?: string;
  userLabel: string;
};

const exact = (path: string) => (pathname: string) => pathname === path;
const prefix = (path: string) => (pathname: string) => pathname.startsWith(path);
const SIDEBAR_CONTEXT_TRANSITION_CLEANUP_MS = 400;
let sidebarContextTransitionTimer: number | undefined;

const beginSidebarContextTransition = (direction: 'backward' | 'forward'): void => {
  document.documentElement.dataset.sidebarContextTransition = direction;
  if (sidebarContextTransitionTimer !== undefined) {
    window.clearTimeout(sidebarContextTransitionTimer);
  }
  sidebarContextTransitionTimer = window.setTimeout(() => {
    if (document.documentElement.dataset.sidebarContextTransition === direction) {
      delete document.documentElement.dataset.sidebarContextTransition;
    }
    sidebarContextTransitionTimer = undefined;
  }, SIDEBAR_CONTEXT_TRANSITION_CLEANUP_MS);
};

const isNavigationItemActive = (item: NavigationItem, pathname: string): boolean =>
  item.active ? item.active(pathname) : exact(item.to)(pathname);

function ProductLink({
  group,
  item,
  pathname,
  preloadRoute,
  userId,
}: {
  group: string;
  item: NavigationItem;
  pathname: string;
  preloadRoute?: (path: string) => void;
  userId?: string;
}): React.ReactElement {
  const { closeMobile, collapsed, isMobile } = useSidebar();
  const Icon = item.icon;
  const active = isNavigationItemActive(item, pathname);

  const handleSelect = () => {
    if (item.contextTransition) beginSidebarContextTransition(item.contextTransition);
    capturePostHogEvent(ANALYTICS_EVENTS.AUTHENTICATED_NAVIGATION_SELECTED, {
      navigation_shell: 'sidebar',
      navigation_group: group,
      navigation_item: item.label,
      source_path: pathname,
      destination_path: item.to,
      user_id: userId,
      is_authenticated: true,
    });
    closeMobile();
  };

  return (
    <SidebarMenuItem>
      <NavLink
        aria-current={active ? 'page' : undefined}
        className="authenticated-sidebar__link"
        onClick={handleSelect}
        onFocus={() => preloadRoute?.(item.to)}
        onMouseEnter={() => preloadRoute?.(item.to)}
        onPointerDown={() => preloadRoute?.(item.to)}
        onTouchStart={() => preloadRoute?.(item.to)}
        title={collapsed && !isMobile ? item.label : undefined}
        to={item.to}
        viewTransition={Boolean(item.contextTransition)}
      >
        <Icon aria-hidden="true" size={17} strokeWidth={1.8} />
        <span>{item.label}</span>
      </NavLink>
    </SidebarMenuItem>
  );
}

function NavigationGroupSection({
  group,
  pathname,
  preloadRoute,
  userId,
}: {
  group: NavigationGroup;
  pathname: string;
  preloadRoute?: (path: string) => void;
  userId?: string;
}): React.ReactElement {
  const items = (
    <SidebarMenu className="authenticated-sidebar__group-menu">
      {group.items.map((item) => (
        <ProductLink
          group={group.label}
          item={item}
          key={item.to}
          pathname={pathname}
          preloadRoute={preloadRoute}
          userId={userId}
        />
      ))}
    </SidebarMenu>
  );

  return (
    <SidebarGroup>
      <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
      {items}
    </SidebarGroup>
  );
}

function NavigationSearch({
  onChange,
  value,
}: {
  onChange: (value: string) => void;
  value: string;
}): React.ReactElement {
  const { collapsed, isMobile, toggle } = useSidebar();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const focusAfterExpandRef = React.useRef(false);

  React.useEffect(() => {
    if (collapsed || !focusAfterExpandRef.current) return;
    focusAfterExpandRef.current = false;
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, [collapsed]);

  if (collapsed && !isMobile) {
    return (
      <button
        aria-label="Search navigation"
        className="authenticated-sidebar__search-trigger"
        onClick={() => {
          focusAfterExpandRef.current = true;
          toggle();
        }}
        title="Search navigation"
        type="button"
      >
        <Search aria-hidden="true" size={17} strokeWidth={1.8} />
      </button>
    );
  }

  return (
    <label className="authenticated-sidebar__search">
      <Search aria-hidden="true" size={15} strokeWidth={1.8} />
      <span className="authenticated-sidebar__search-label">Search navigation</span>
      <SidebarInput
        aria-label="Search navigation"
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && value) {
            event.preventDefault();
            onChange('');
          }
        }}
        placeholder="Search"
        ref={inputRef}
        type="search"
        value={value}
      />
    </label>
  );
}

function AccountBackLink({
  pathname,
  userId,
}: {
  pathname: string;
  userId?: string;
}): React.ReactElement {
  const { closeMobile } = useSidebar();

  const handleSelect = () => {
    beginSidebarContextTransition('backward');
    capturePostHogEvent(ANALYTICS_EVENTS.AUTHENTICATED_NAVIGATION_SELECTED, {
      navigation_shell: 'sidebar',
      navigation_group: 'Account',
      navigation_item: 'Back to BotSpot',
      source_path: pathname,
      destination_path: '/dashboard',
      user_id: userId,
      is_authenticated: true,
    });
    closeMobile();
  };

  return (
    <NavLink
      aria-label="Back to BotSpot"
      className="authenticated-sidebar__header-back"
      onClick={handleSelect}
      title="Back to BotSpot"
      to="/dashboard"
      viewTransition
    >
      <ArrowLeft aria-hidden="true" size={17} strokeWidth={1.8} />
    </NavLink>
  );
}

export default function AuthenticatedSidebar({
  aiBootcampPath,
  challengesPath,
  currentPath,
  logoSrc,
  preloadRoute,
  userId,
  userLabel,
}: AuthenticatedSidebarProps): React.ReactElement {
  const [websiteTheme] = useThemeMode();
  const productGroups: NavigationGroup[] = [
    {
      context: 'product',
      label: 'Home',
      items: [
        { icon: ChartNoAxesCombined, label: 'Dashboard', to: '/dashboard' },
        { icon: Bot, label: 'AI Agent', to: '/ai-agent' },
        { icon: Plug, label: 'Connect your AI agent', to: '/agents' },
        { icon: Store, label: 'Marketplace', active: prefix('/marketplace'), to: '/marketplace' },
        {
          icon: UsersRound,
          label: 'Community',
          active: (path) => ['/community', '/posts/'].some((route) => path.startsWith(route)),
          to: '/community',
        },
        {
          icon: GraduationCap,
          label: 'AI Bootcamp',
          active: (path) => path.startsWith('/courses/ai-trading-bootcamp'),
          to: aiBootcampPath,
        },
        { icon: Flag, label: 'Challenges', active: prefix('/challenges'), to: challengesPath },
      ],
    },
    {
      context: 'product',
      label: 'Library',
      items: [
        {
          icon: Landmark,
          label: 'Accounts',
          active: prefix('/brokerage-accounts'),
          to: '/brokerage-accounts',
        },
        { icon: Layers3, label: 'Strategies', to: '/my-strategies' },
        { icon: BarChart3, label: 'Backtests', to: '/my-backtests' },
        { icon: Rocket, label: 'Bots', to: '/my-bots' },
        { icon: ChartNoAxesCombined, label: 'Charts', active: prefix('/charts'), to: '/charts' },
      ],
    },
    {
      context: 'product',
      label: 'Learn',
      items: [
        { icon: BookOpen, label: 'My Courses', active: prefix('/my-courses'), to: '/my-courses' },
        {
          icon: CalendarDays,
          label: 'Live Algo Lab',
          active: prefix('/live-algo-lab'),
          to: '/live-algo-lab',
        },
        { icon: Trophy, label: 'Trading Competition', to: '/hackathon' },
        { icon: Newspaper, label: 'Blog', active: prefix('/blog'), to: '/blog' },
      ],
    },
    {
      context: 'product',
      label: 'More',
      items: [
        { icon: Building2, label: 'Enterprise', to: '/enterprise' },
        { icon: CreditCard, label: 'Pricing', to: '/pricing' },
        { icon: Handshake, label: 'Affiliate', to: '/affiliate' },
      ],
    },
  ];
  const accountGroups: NavigationGroup[] = [
    {
      context: 'account',
      label: 'Personal',
      items: [
        { icon: UserRound, label: 'My Profile', to: '/account-settings/profile' },
        { icon: Palette, label: 'Appearance', to: '/account-settings/appearance' },
        { icon: ShieldCheck, label: 'Security', to: '/account-settings/security' },
        { icon: Bell, label: 'Notifications', to: '/account-settings/notifications' },
      ],
    },
    {
      context: 'account',
      label: 'Connections',
      items: [
        { icon: KeyRound, label: 'API Keys', to: '/account-settings/api-keys' },
        { icon: Plug, label: 'Third-party Connections', to: '/account-settings/connections' },
        { icon: LockKeyhole, label: 'Saved Variables', to: '/account-settings/saved-env-vars' },
        { icon: Landmark, label: 'Broker Connections', to: '/account-settings/broker-connections' },
      ],
    },
    {
      context: 'account',
      label: 'Plan',
      items: [
        {
          icon: CreditCard,
          label: 'Subscriptions & Billing',
          to: '/account-settings/subscriptions',
        },
        { icon: BarChart3, label: 'Usage', to: '/account-settings/usage' },
      ],
    },
    ...(isExperimentPreviewAvailable()
      ? [
          {
            context: 'account' as const,
            label: 'Development',
            items: [
              { icon: FlaskConical, label: 'Experiments', to: '/account-settings/experiments' },
            ],
          },
        ]
      : []),
  ];
  const { collapsed, isMobile } = useSidebar();
  const accountActive = currentPath.startsWith('/account-settings');
  const [navigationSearch, setNavigationSearch] = React.useState('');
  const currentNavigationContext: NavigationContext = accountActive ? 'account' : 'product';
  const navigationGroups = accountActive ? accountGroups : productGroups;
  const normalizedNavigationSearch = navigationSearch.trim().toLocaleLowerCase();
  const visibleGroups = normalizedNavigationSearch
    ? [...productGroups, ...accountGroups]
        .map((group) => ({
          ...group,
          label: `${group.context === 'account' ? 'Account' : 'BotSpot'} / ${group.label}`,
          items: group.items
            .filter((item) =>
              [
                item.label,
                group.label,
                group.context === 'account' ? 'Account settings' : 'BotSpot',
              ].some((searchTerm) =>
                searchTerm.toLocaleLowerCase().includes(normalizedNavigationSearch),
              ),
            )
            .map((item) => ({
              ...item,
              contextTransition:
                group.context === currentNavigationContext
                  ? item.contextTransition
                  : group.context === 'account'
                    ? 'forward'
                    : 'backward',
            })),
        }))
        .filter((group) => group.items.length > 0)
    : navigationGroups;

  React.useEffect(() => {
    setNavigationSearch('');
  }, [accountActive]);

  const handleAccountSelect = () => {
    if (!accountActive) beginSidebarContextTransition('forward');
    capturePostHogEvent(ANALYTICS_EVENTS.AUTHENTICATED_NAVIGATION_SELECTED, {
      navigation_shell: 'sidebar',
      navigation_group: 'Account',
      navigation_item: 'Account settings',
      source_path: currentPath,
      destination_path: '/account-settings/profile',
      user_id: userId,
      is_authenticated: true,
    });
  };

  return (
    <>
      <div
        className="authenticated-sidebar__mobile-launcher bsui-scope"
        data-bs-theme={websiteTheme}
      >
        <SidebarTrigger mobile />
      </div>
      <Sidebar
        className="authenticated-sidebar bsui-scope"
        data-bs-theme={websiteTheme}
        id="authenticated-product-sidebar"
        role="navigation"
      >
        <SidebarHeader className="authenticated-sidebar__header">
          <div className="authenticated-sidebar__header-row">
            <NavLink
              aria-label="BotSpot dashboard"
              className="authenticated-sidebar__brand"
              onClick={() => {
                if (accountActive) beginSidebarContextTransition('backward');
              }}
              to="/dashboard"
              viewTransition={accountActive}
            >
              <span className="authenticated-sidebar__brand-clip">
                <img className="botspot-logo-img--base" alt="BotSpot by LumiWealth" src={logoSrc} />
                <img
                  className="authenticated-sidebar__mascot"
                  alt=""
                  aria-hidden="true"
                  src={logoSrc}
                />
              </span>
            </NavLink>
            <SidebarTrigger />
          </div>
          <div
            className={`authenticated-sidebar__search-row${
              accountActive ? ' authenticated-sidebar__search-row--account' : ''
            }`}
          >
            {accountActive ? <AccountBackLink pathname={currentPath} userId={userId} /> : null}
            <NavigationSearch onChange={setNavigationSearch} value={navigationSearch} />
          </div>
        </SidebarHeader>
        <SidebarContent>
          <div
            className="authenticated-sidebar__navigation-context"
            data-navigation-context={accountActive ? 'account' : 'product'}
          >
            {visibleGroups.map((group) => (
              <NavigationGroupSection
                group={group}
                key={`${group.context}:${group.label}`}
                pathname={currentPath}
                preloadRoute={preloadRoute}
                userId={userId}
              />
            ))}
            {normalizedNavigationSearch && visibleGroups.length === 0 ? (
              <p className="authenticated-sidebar__search-empty" role="status">
                No navigation matches “{navigationSearch.trim()}”.
              </p>
            ) : null}
          </div>
        </SidebarContent>
        <SidebarFooter className="authenticated-sidebar__footer">
          <NavLink
            aria-label={`Account settings for ${userLabel}`}
            className="authenticated-sidebar__identity"
            onClick={handleAccountSelect}
            title={collapsed && !isMobile ? 'Account settings' : undefined}
            to="/account-settings/profile"
            viewTransition={!accountActive}
          >
            <span className="authenticated-sidebar__avatar" aria-hidden="true">
              {userLabel.trim().charAt(0).toUpperCase() || 'U'}
            </span>
            <span className="authenticated-sidebar__identity-copy">
              <span className="authenticated-sidebar__identity-label">{userLabel}</span>
              <span className="authenticated-sidebar__identity-meta">Account settings</span>
            </span>
          </NavLink>
        </SidebarFooter>
      </Sidebar>
    </>
  );
}
